import { isNil, isNumber, isString } from 'lodash';
import { API } from '@shared/types/api';
import { CardMatch, toCardMatch } from './cardMatch';
import { CardMatchLookups } from './cardMatchLookups';
import { getStandardTokens, matchesPhrasePrefix } from './phrasePrefix';

// core-api also searches the Card ID for a query of up to ten digits that doesn't start with zero
const CARD_ID_QUERY = /^[1-9]\d{0,9}$/;
const CARD_ID_COMPONENT_ID = 'sequenceNumber';

/**
 * Whether a card passes a list query's `omniSearchQuery`, evaluated the way core-api's search
 * applies it: a phrase prefix match within any one of the template's text fields, or a Card ID
 * starting with the query's digits. Archived components aren't searched.
 *
 * Any query that isn't null applies, so an empty one matches nothing.
 */
export function getCardSearchMatch(
  card: API.WorkflowEntity,
  query: string | null | undefined,
  lookups: CardMatchLookups,
): CardMatch {
  if (isNil(query)) {
    return 'match';
  }
  const components = lookups.getWorkflowTemplate(card.workflowTemplateId)?.components;
  if (isNil(components)) {
    return 'unknown';
  }

  const queryTokens = getStandardTokens(query);
  const matchesText = components.some(component => {
    const fieldValue = card.fields?.[component.id];
    return (
      component.type === 'TEXT' &&
      isString(fieldValue) &&
      matchesPhrasePrefix(getStandardTokens(fieldValue), queryTokens)
    );
  });
  if (matchesText) {
    return 'match';
  }

  const hasCardId = components.some(component => component.id === CARD_ID_COMPONENT_ID);
  const cardId = card.fields?.[CARD_ID_COMPONENT_ID];
  return toCardMatch(
    hasCardId && CARD_ID_QUERY.test(query) && isNumber(cardId) && String(cardId).startsWith(query),
  );
}
